<template>
  <Dialog ref="dialog">
    <template #expander="{ openModal }">
      <button
        class="rpt-btn rpt-filter-btn"
        @click="() => { restoreSelection(); openModal(); }"
      >
        <svg class="rpt-filter-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="m21 15-5-5L5 21" />
        </svg>
        <span v-if="selectedCount">{{ selectedCount }} Media</span>
        <span v-else>All Media</span>
      </button>
    </template>
    <template #dialog.content>
      <SimpleEntityListingPage
        entityItemsNamePlural="Media"
        entityItemName="Media"
        areaAccessName="MediaLibrary"
        :ItemsLister="MediaLister"
        :selectMode="true"
      />
      <div class="rpt-dialog-actions">
        <ButtonCustomized label="Cancel" :secondary="true" @click="onCancel" />
        <ButtonCustomized label="Clear Filter" :secondary="true" @click="onClear" />
        <ButtonCustomized label="Apply Filter" @click="onApply" />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import ButtonCustomized from '@/components/form-fields/ButtonCustomized.vue';
import SimpleEntityListingPage from '@/components/ListingPages/SimpleEntityListingPage/SimpleEntityListingPage.vue';
import MediaLister from '@/views/MediaListView/MediaLister/MediaLister.vue';
import useListSelection from '@/components/ListingPages/ItemsLister/ItemsList/composables/listSelection';

interface SelectableEntity {
  id: number | string;
  [key: string]: unknown;
}

const emit = defineEmits<{
  // Empty array == clear filter.
  (e: 'apply', mediaIds: string[]): void;
}>();

const dialog = ref<InstanceType<typeof Dialog> | null>(null);
// Last-applied selection, restored when the dialog reopens.
const savedItems = ref<SelectableEntity[]>([]);

const selectedCount = computed(() => savedItems.value.length);

// useListSelection is a singleton across the app — see CODE_REVIEW.md §1.3.
// Since only one filter dialog is open at a time, we save/restore against
// our own savedItems on open/close to avoid bleed between dialogs.
const {
  selectedItems: dialogSelectedItems,
  clearItemsSelection: clearDialogSelection,
  addMultipleItemsToSelection: addMultipleToDialogSelection,
} = useListSelection();

// ---------------------------------------------------------------------------
// KNOWN WORKAROUND — see CODE_REVIEW.md §1.3
// ---------------------------------------------------------------------------
// useListSelection is a module-level singleton across the whole app, and it
// installs a global click listener on document.body that clears the current
// selection on any "outside" click. The expander button click that opens this
// dialog *is* one of those outside clicks — it bubbles up to document.body
// after our restore runs, so anything we do synchronously gets wiped.
//
// setTimeout(0) defers our restore to a macrotask that runs AFTER the click
// has finished bubbling and the global listener has cleared. We then
// re-populate on top of an already-cleared state, and it sticks.
//
// Must be setTimeout (macrotask), NOT nextTick / Promise.resolve (microtask) —
// microtasks interleave with event bubbling and would race the listener.
//
// HOW TO REMOVE THIS WORKAROUND:
//   1. Extend useListSelection with an opt-in `{ scoped: true }` option that:
//      - returns per-instance state (not the module-level refs), and
//      - skips wiring the document.body click listener.
//   2. Switch this dialog (and PlayerFilterDialog) to useListSelection({ scoped: true }).
//   3. Delete this setTimeout block — restoreSelection becomes synchronous.
//
// The composable change touches every list page in the app, so it's a
// separate initiative — not bundled with the Reports refactor.
// ---------------------------------------------------------------------------
function restoreSelection() {
  setTimeout(() => {
    clearDialogSelection();
    if (savedItems.value.length) {
      addMultipleToDialogSelection(savedItems.value, {
        itemIdGetterCallback: (item: SelectableEntity) => item.id,
      });
    }
  }, 0);
}

function onApply() {
  if (dialogSelectedItems.length === 0) {
    savedItems.value = [];
    emit('apply', []);
  } else {
    const items = [...dialogSelectedItems] as SelectableEntity[];
    savedItems.value = items;
    emit('apply', items.map((item) => String(item.id)));
  }
  dialog.value?.closeModal();
}

function onCancel() {
  dialog.value?.closeModal();
}

function onClear() {
  savedItems.value = [];
  clearDialogSelection();
  emit('apply', []);
  dialog.value?.closeModal();
}

defineExpose({ open: () => dialog.value?.openModal() });
</script>

<style scoped>
@import '@/views/ReportsView/styles/reports-shared.css';

.rpt-filter-btn {
  gap: 0.4rem;
  font-weight: 500;
}

.rpt-filter-btn .rpt-filter-icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.rpt-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e5e7eb;
}
</style>
